import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Button, NumberField, SelectField } from "@mapmycustomers/ui";

import styles from "./Field.module.scss";

const STANDARD_RANGES = [7, 14, 30, 60, 90];

const CUSTOM_VALUE = -1;
const NEVER_CONTACTED = -2;

const messages = defineMessages({
  custom: {
    id: "dashboard.editModal.dateRange.noContactIn.dateRange.option.custom",
    defaultMessage: "Custom",
    description:
      "Custom option in dropdown for Date range field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  customValueLabel: {
    id: "dashboard.editModal.dateRange.noContactIn.dateRange.custom.input",
    defaultMessage: "more than {input} {count, plural, one {day} other {days}}",
    description:
      "Label around the input custom value for Date range field in a criteria form in Edit New No Contact In on Dashboard",
  },
  neverContacted: {
    id: "dashboard.editModal.dateRange.noContactIn.dateRange.option.neverContacted",
    defaultMessage: "Never Contacted",
    description:
      "Never Contacted option in dropdown for Date range field in a criteria form in Edit No Contact In Modal on Dashboard",
  },
  optionLabel: {
    id: "dashboard.editModal.dateRange.noContactIn.dateRange.option",
    defaultMessage: "> {count} {count, plural, one {day} other {days}}",
    description:
      "Option in dropdown for Date range field in a criteria form in Edit New No Contact In on Dashboard",
  },
  returnToList: {
    id: "dashboard.editModal.dateRange.noContactIn.dateRange.custom.returnToList",
    defaultMessage: "Select from list",
    description:
      "Return to predefined options link in Date range field in a criteria form in Edit New No Contact In on Dashboard",
  },
});

interface Props {
  disabled?: boolean;
  onChange?: (value: null | number) => void;
  value?: null | number;
}

const Field: React.FC<Props> = ({ disabled, onChange, value }) => {
  const intl = useIntl();

  const [customMode, setCustomMode] = useState(false);
  useEffect(
    () => {
      setCustomMode(!!value && !STANDARD_RANGES.includes(value));
    },
    // we're not having any deps here intentionally, to only run this useEffect one time
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleExitCustomMode = useCallback(() => setCustomMode(false), []);

  const options = useMemo(
    () => [
      ...STANDARD_RANGES.map((value) => ({
        label: intl.formatMessage(messages.optionLabel, { count: value }),
        value,
      })),
      { label: intl.formatMessage(messages.neverContacted), value: NEVER_CONTACTED },
      { label: intl.formatMessage(messages.custom), value: CUSTOM_VALUE },
    ],
    [intl]
  );

  const handleCustomValueChange = useCallback(
    (value?: number) => isDefined(value) && onChange?.(value),
    [onChange]
  );

  const handleValueChange = useCallback(
    (value) => {
      if (value === CUSTOM_VALUE) {
        setCustomMode(true);
      } else {
        onChange?.(value === NEVER_CONTACTED ? null : value);
      }
    },
    [onChange]
  );

  return !customMode ? (
    <SelectField<number>
      disabled={disabled}
      onChange={handleValueChange}
      options={options}
      showArrow
      suffixIcon={disabled ? <FontAwesomeIcon icon={faLock} /> : undefined}
      value={value === null ? NEVER_CONTACTED : value}
    />
  ) : (
    <div className={styles.custom}>
      <div className={styles.valueInput}>
        {intl.formatMessage(messages.customValueLabel, {
          count: value,
          input: (
            <NumberField
              disabled={disabled}
              min={1}
              onChange={handleCustomValueChange}
              precision={0}
              step={1}
              value={value}
            />
          ),
        })}
      </div>
      <Button disabled={disabled} onClick={handleExitCustomMode} type="text">
        {intl.formatMessage(messages.returnToList)}
      </Button>
    </div>
  );
};

export default Field;
